import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../api/auth';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 p-4">
        <div className="w-full max-w-md">
          <Card variant="elevated">
            <Card.Header
              title="Check Your Email"
              subtitle="Password reset instructions sent"
            />
            <Card.Body>
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-success/15 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-gray-700 dark:text-gray-300">
                  <p className="mb-2">
                    If an account exists for <strong>{email}</strong>, you will receive an email with password reset instructions.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    The link will expire in 24 hours.
                  </p>
                </div>
              </div>
            </Card.Body>
            <Card.Footer>
              <div className="text-center">
                <Link to="/login" className="text-primary dark:text-primary-400 hover:text-primary/80 dark:hover:text-primary-300 font-medium">
                  Return to login
                </Link>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 p-4">
      <div className="w-full max-w-md">
        <Card variant="elevated">
          <Card.Header
            title="Forgot Password?"
            subtitle="Enter your email to reset your password"
          />
          <Card.Body>
            {error && (
              <div className="mb-4 rounded-lg p-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                id="email"
                type="email"
                label="Email Address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="Enter your email address"
                autoComplete="email"
                helperText="We'll send you a link to reset your password."
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading || !email}
                className="w-full"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </Card.Body>
          <Card.Footer>
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Remember your password?{' '}
              <Link to="/login" className="text-primary dark:text-primary-400 hover:text-primary/80 dark:hover:text-primary-300 font-medium">
                Sign in
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
